/** Evaluación local de No molestar SoftPhone (alineada al servidor). */

export type SoftphoneDndScope = 'phone' | 'doorbell' | 'both';
export type SoftphoneDndSource = 'manual' | 'schedule' | null;

export interface SoftphoneDndSegment {
  day: number; // Mon=0 … Sun=6
  start_min: number;
  end_min: number;
}

export interface SoftphoneDndRule {
  id: string;
  enabled: boolean;
  days: number[];
  start: string;
  end: string;
  scope: SoftphoneDndScope;
  client_timezone?: string;
  segments?: SoftphoneDndSegment[];
}

export interface SoftphoneDndEffective {
  active: boolean;
  source: SoftphoneDndSource;
  scope?: SoftphoneDndScope | null;
  color?: 'red' | 'orange' | null;
}

export function clientTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function scopeCovers(scope: SoftphoneDndScope | string | undefined, kind: string): boolean {
  const s = (scope || 'both').toLowerCase();
  const k = (kind || 'phone').toLowerCase();
  if (s === 'both') return true;
  return s === k;
}

/** Minutos y weekday (Mon=0) en una TZ IANA. */
export function wallClockInTz(date: Date, timeZone: string): { day: number; mins: number } {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const parts = fmt.formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value || '';
  const wd = get('weekday'); // Sun, Mon, …
  const map: Record<string, number> = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
  const day = map[wd] ?? 0;
  const hour = parseInt(get('hour'), 10) || 0;
  const minute = parseInt(get('minute'), 10) || 0;
  return { day, mins: hour * 60 + minute };
}

export function segmentsActiveNow(
  segments: SoftphoneDndSegment[] | undefined,
  serverTimezone: string,
  now = new Date(),
): boolean {
  if (!segments?.length) return false;
  const { day, mins } = wallClockInTz(now, serverTimezone || 'UTC');
  return segments.some((seg) => seg.day === day && seg.start_min <= mins && mins < seg.end_min);
}

/** Fallback si aún no hay segments (solo display days/start/end en client TZ). */
export function displayRuleActiveNow(rule: SoftphoneDndRule, now = new Date()): boolean {
  if (!rule.enabled) return false;
  const tz = rule.client_timezone || clientTimezone();
  const { day, mins } = wallClockInTz(now, tz);
  if (!rule.days.includes(day)) return false;
  const [sh, sm] = (rule.start || '00:00').split(':').map((x) => parseInt(x, 10) || 0);
  const [eh, em] = (rule.end || '00:00').split(':').map((x) => parseInt(x, 10) || 0);
  const start = sh * 60 + sm;
  const end = eh * 60 + em;
  if (start === end) return false;
  if (end > start) return mins >= start && mins < end;
  // overnight
  return mins >= start || mins < end;
}

export function evaluateLocalDnd(opts: {
  manualEnabled: boolean;
  manualScope: SoftphoneDndScope;
  rules: SoftphoneDndRule[];
  kind: string;
  serverTimezone: string;
}): SoftphoneDndEffective & { blocked: boolean } {
  const { manualEnabled, manualScope, rules, kind, serverTimezone } = opts;
  if (manualEnabled && scopeCovers(manualScope, kind)) {
    return { blocked: true, active: true, source: 'manual', color: 'red', scope: manualScope };
  }
  for (const rule of rules) {
    if (!rule.enabled) continue;
    if (!scopeCovers(rule.scope, kind)) continue;
    const active = rule.segments?.length
      ? segmentsActiveNow(rule.segments, serverTimezone)
      : displayRuleActiveNow(rule);
    if (active) {
      return { blocked: true, active: true, source: 'schedule', color: 'orange', scope: rule.scope };
    }
  }
  return { blocked: false, active: false, source: null, color: null, scope: null };
}

export function headerIndicator(opts: {
  manualEnabled: boolean;
  manualScope: SoftphoneDndScope;
  rules: SoftphoneDndRule[];
  serverTimezone: string;
}): SoftphoneDndEffective {
  if (opts.manualEnabled) {
    return { active: true, source: 'manual', color: 'red', scope: opts.manualScope };
  }
  for (const rule of opts.rules) {
    if (!rule.enabled) continue;
    const active = rule.segments?.length
      ? segmentsActiveNow(rule.segments, opts.serverTimezone)
      : displayRuleActiveNow(rule);
    if (active) {
      return { active: true, source: 'schedule', color: 'orange', scope: rule.scope };
    }
  }
  return { active: false, source: null, color: null, scope: null };
}
